import {CommonModule} from '@angular/common';
import {Component, EventEmitter, OnInit, Output, inject} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {AccountSecurityService} from '@gatewee/services/account-security.service';
import {
  twoFactorCancelledError,
  twoFactorMaxAttemptsError,
} from '@gatewee/services/two-factor-auth.service';
import {TwoFactorSetupResponse} from '@gatewee/types/account-security';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {TuiAlertService, TuiDialogService, TuiTextfield} from '@taiga-ui/core';
import {TuiInputPin} from '@taiga-ui/kit';
import {PolymorpheusComponent} from '@taiga-ui/polymorpheus';
import {catchError, finalize, of} from 'rxjs';
import {
  GateweeConfirmDialogComponent,
  GateweeConfirmDialogData,
} from '../gatewee-confirm-dialog/gatewee-confirm-dialog.component';
import {GateweeButtonComponent} from '../gatewee-button/gatewee-button.component';
import {GateweeInputV2Component} from '../gatewee-input-v2/gatewee-input-v2.component';

@Component({
  selector: 'gatewee-profile-security',
  templateUrl: './gatewee-profile-security.component.html',
  styleUrls: ['./gatewee-profile-security.component.css'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    TuiTextfield,
    TuiInputPin,
    GateweeButtonComponent,
    GateweeInputV2Component,
  ],
})
export class GateweeProfileSecurityComponent implements OnInit {
  @Output() readonly securityChanged = new EventEmitter<void>();

  passwordForm!: FormGroup;
  twoFactorEnabled = false;
  twoFactorLoading = true;
  twoFactorActionLoading = false;
  passwordSubmitting = false;
  setupPreview: TwoFactorSetupResponse | null = null;
  readonly setupCodeControl = new FormControl('', {
    nonNullable: true,
  });

  private readonly fb = inject(FormBuilder);
  private readonly accountSecurity = inject(AccountSecurityService);
  private readonly alerts = inject(TuiAlertService);
  private readonly dialogService = inject(TuiDialogService);
  private readonly translate = inject(TranslateService);

  ngOnInit(): void {
    this.passwordForm = this.fb.group({
      oldPassword: ['', [Validators.required]],
      newPassword: ['', [Validators.required, Validators.minLength(6)]],
      confirmPassword: ['', [Validators.required]],
    });

    // this.loadTwoFactorStatus();
  }

  get isSetupCodeValid(): boolean {
    return this.setupCodeControl.value.trim().length === 6;
  }

  get passwordsMismatch(): boolean {
    const password = String(this.passwordForm.get('newPassword')?.value ?? '');
    const confirm = String(
      this.passwordForm.get('confirmPassword')?.value ?? '',
    );
    return confirm.length > 0 && password !== confirm;
  }

  loadTwoFactorStatus(): void {
    this.twoFactorLoading = true;
    this.accountSecurity
      .getTwoFactorStatus()
      .pipe(
        catchError(() => of({enabled: false})),
        finalize(() => {
          this.twoFactorLoading = false;
        }),
      )
      .subscribe(status => {
        this.twoFactorEnabled = !!status.enabled;
      });
  }

  submitPasswordChange(): void {
    if (this.passwordForm.invalid || this.passwordsMismatch) {
      this.passwordForm.markAllAsTouched();
      return;
    }

    const {oldPassword, newPassword} = this.passwordForm.getRawValue();
    this.passwordSubmitting = true;
    this.accountSecurity
      .changePasswordWith2Fa({oldPassword, newPassword})
      .pipe(
        finalize(() => {
          this.passwordSubmitting = false;
        }),
      )
      .subscribe({
        next: () => {
          this.passwordForm.reset();
          this.alerts
            .open(
              this.translate.instant('features.security.password.success'),
              {
                label: this.translate.instant('common.success'),
                appearance: 'success',
              },
            )
            .subscribe();
        },
        error: err => {
          if (
            err instanceof Error &&
            (err.message === twoFactorCancelledError ||
              err.message === twoFactorMaxAttemptsError)
          ) {
            if (err.message === twoFactorMaxAttemptsError) {
              this.alerts
                .open(
                  this.translate.instant(
                    'common.twoFactor.maxAttemptsExceeded',
                  ),
                  {
                    label: this.translate.instant('common.error'),
                    appearance: 'error',
                  },
                )
                .subscribe();
            }
            return;
          }
          this.alerts
            .open(this.resolveErrorMessage(err), {
              label: this.translate.instant('common.error'),
              appearance: 'error',
            })
            .subscribe();
        },
      });
  }

  startTwoFactorSetup(): void {
    this.twoFactorActionLoading = true;
    this.setupPreview = null;
    this.setupCodeControl.setValue('');
    this.accountSecurity
      .enableTwoFactor()
      .pipe(
        finalize(() => {
          this.twoFactorActionLoading = false;
        }),
      )
      .subscribe({
        next: setup => {
          this.setupPreview = setup;
        },
        error: err => {
          this.alerts
            .open(this.resolveErrorMessage(err), {
              label: this.translate.instant('common.error'),
              appearance: 'error',
            })
            .subscribe();
        },
      });
  }

  cancelTwoFactorSetup(): void {
    this.setupPreview = null;
    this.setupCodeControl.setValue('');
  }

  confirmTwoFactorSetup(): void {
    if (!this.isSetupCodeValid) {
      return;
    }

    this.twoFactorActionLoading = true;
    this.accountSecurity
      .confirmTwoFactor(this.setupCodeControl.value.trim())
      .pipe(
        finalize(() => {
          this.twoFactorActionLoading = false;
        }),
      )
      .subscribe({
        next: () => {
          this.setupPreview = null;
          this.setupCodeControl.setValue('');
          this.twoFactorEnabled = true;
          this.securityChanged.emit();
          this.alerts
            .open(
              this.translate.instant(
                'features.security.twoFactor.enableSuccess',
              ),
              {
                label: this.translate.instant('common.success'),
                appearance: 'success',
              },
            )
            .subscribe();
        },
        error: err => {
          this.alerts
            .open(this.resolveErrorMessage(err), {
              label: this.translate.instant('common.error'),
              appearance: 'error',
            })
            .subscribe();
        },
      });
  }

  disableTwoFactor(): void {
    this.dialogService
      .open<boolean>(new PolymorpheusComponent(GateweeConfirmDialogComponent), {
        data: {
          title: this.translate.instant(
            'features.security.twoFactor.disableConfirmTitle',
          ),
          message: this.translate.instant(
            'features.security.twoFactor.disableConfirmMessage',
          ),
          confirmLabel: this.translate.instant('common.actions.confirm'),
          cancelLabel: this.translate.instant('common.actions.cancel'),
        } satisfies GateweeConfirmDialogData,
        dismissible: true,
      })
      .subscribe(confirmed => {
        if (!confirmed) {
          return;
        }
        void this.runDisableTwoFactor();
      });
  }

  private async runDisableTwoFactor(): Promise<void> {
    this.twoFactorActionLoading = true;
    try {
      await this.accountSecurity.disableTwoFactor();
      this.twoFactorEnabled = false;
      this.securityChanged.emit();
      this.alerts
        .open(
          this.translate.instant('features.security.twoFactor.disableSuccess'),
          {
            label: this.translate.instant('common.success'),
            appearance: 'success',
          },
        )
        .subscribe();
    } catch (error) {
      if (
        error instanceof Error &&
        (error.message === twoFactorCancelledError ||
          error.message === twoFactorMaxAttemptsError)
      ) {
        if (error.message === twoFactorMaxAttemptsError) {
          this.alerts
            .open(
              this.translate.instant('common.twoFactor.maxAttemptsExceeded'),
              {
                label: this.translate.instant('common.error'),
                appearance: 'error',
              },
            )
            .subscribe();
        }
        return;
      }
      this.alerts
        .open(this.resolveErrorMessage(error), {
          label: this.translate.instant('common.error'),
          appearance: 'error',
        })
        .subscribe();
    } finally {
      this.twoFactorActionLoading = false;
    }
  }

  private resolveErrorMessage(error: unknown): string {
    if (
      error &&
      typeof error === 'object' &&
      'error' in error &&
      (error as {error?: {message?: string}}).error?.message
    ) {
      return String((error as {error: {message: string}}).error.message);
    }
    return this.translate.instant('common.errors.unknown');
  }
}
